import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAppSelector } from "../../../app/store/store";
import { calendarService, ICalendarFilter, IGetCalendarFilterOptionsResponse } from "../../../entities/calendar";
import { useGlobalMessageActions } from "../../../entities/globalMessage";
import { FEATURES, hasFeature, useMyActions } from "../../../entities/my";
import { studentService } from "../../../entities/student";
import { getTrialCalendarFilter, getTrialOptionItems, getTrialRequestErrorMessage,
    getTrialSelectedIdItems, IAvailableTrialTutor, ITrialCalendarFilter,
    trialLessonService, TTutorGender } from "../../../entities/trialLesson";
import { workTgService } from "../../../entities/workTg";
import { MyError } from "../../../shared/lib/error/MyError";
import { AuthError } from "../../../shared/lib/error/AuthError";
import { IItem } from "../../../shared/model/types";
import { MyButton } from "../../../shared/ui/button";
import { DropDownListSelected } from "../../../shared/ui/dropDownSelected";
import { MyInput } from "../../../shared/ui/input";
import { MyTextarea } from "../../../shared/ui/myTextarea";
import { TrialCreateOptionModal } from "./TrialCreateOptionModal";
import { formatTutorConversion, formatTutorLoad } from "../../../shared/lib/helpers/formatTutorLoad";
import classes from './trialLessonCreate.module.scss'

interface IProps {
    startsAt: Date;
    durationSlots: number;
    filter: ITrialCalendarFilter;
    filterOptions: IGetCalendarFilterOptionsResponse | null;
    formatTime: (date: Date) => string;
    onCreated: () => void;
    onCancel: () => void;
    onMount?: () => void;
}

type TCreateStage = 'tgAccountId' | 'tagId' | 'userId' | null

const GENDER_ITEMS: Array<IItem<TTutorGender>> = [
    {id: 'TUTOR_GENDER_ANY', name: 'Любой'},
    {id: 'TUTOR_GENDER_MALE', name: 'Мужской'},
    {id: 'TUTOR_GENDER_FEMALE', name: 'Женский'},
]

export const TrialLessonCreate: FC<IProps> = ({
    startsAt, durationSlots, filter, filterOptions, formatTime, onCreated, onCancel, onMount,
}) => {

    const initialTgAccountIdRef = useRef<string>(filter.tgAccountIds.length === 1 ? filter.tgAccountIds[0] : '')
    const initialUserIdRef = useRef<string>(filter.userIds.length === 1 ? filter.userIds[0] : '')
	const initialTagIdRef = useRef<string>(filter.tagIds.length === 1 ? filter.tagIds[0] : '')
	const initialTutorGenderRef = useRef<TTutorGender>(filter.tutorGender)

    const [tgAccountId, setTgAccountId] = useState<string>(() => initialTgAccountIdRef.current)
    const [selectedTagId, setSelectedTagId] = useState<string>(() => initialTagIdRef.current)
	const [tutorGender, setTutorGender] = useState<TTutorGender>(() => initialTutorGenderRef.current)
    const [userId, setUserId] = useState<string>('')
    const [tag, setTag] = useState<string>('')
    const [tgAccountOptions, setTgAccountOptions] = useState<IGetCalendarFilterOptionsResponse['tgAccounts']>(
        filterOptions?.tgAccounts ?? []
    )
    const [calendarTagOptions, setCalendarTagOptions] = useState<IGetCalendarFilterOptionsResponse['tags']>(
        filterOptions?.tags ?? []
    )
    const [availableTutors, setAvailableTutors] = useState<IAvailableTrialTutor[]>([])
    const [studentName, setStudentName] = useState<string>('')
    const [studentTg, setStudentTg] = useState<string>('')
    const [privateInfo, setPrivateInfo] = useState<string>('')
    const [publicComment, setPublicComment] = useState<string>('')
    const [isSaving, setIsSaving] = useState<boolean>(false)
    const [isCreateTagOpen, setIsCreateTagOpen] = useState<boolean>(false)
    const [createTagValue, setCreateTagValue] = useState<string>('')
    const [createTagError, setCreateTagError] = useState<string>('')
    const [isTagCreating, setIsTagCreating] = useState<boolean>(false)
    const [isCreateWorkTgOpen, setIsCreateWorkTgOpen] = useState<boolean>(false)
    const [createWorkTgValue, setCreateWorkTgValue] = useState<string>('')
    const [createWorkTgError, setCreateWorkTgError] = useState<string>('')
    const [isWorkTgCreating, setIsWorkTgCreating] = useState<boolean>(false)
    const [stage, setStage] = useState<TCreateStage>('tgAccountId')
    const [error, setError] = useState<string>('')
    const optionsControllerRef = useRef<AbortController | null>(null)
    const availableTutorsControllerRef = useRef<AbortController | null>(null)

    const {setGlobalMessage} = useGlobalMessageActions()
    const {setIsAuth} = useMyActions()
    const {my} = useAppSelector(state => state.myReducer)
    const canCreateTag = hasFeature(my, FEATURES.STUDENTS.TAGS_CREATE)
    const canCreateWorkTg = hasFeature(my, FEATURES.WORK_TGS.CREATE)

    const endsAt = new Date(startsAt.getTime() + durationSlots * 30 * 60 * 1000)
    const tgAccountItems = useMemo<IItem<string>[]>(() => (
        getTrialOptionItems(
            tgAccountOptions,
            tgAccount => tgAccount.title || `@${tgAccount.username}`,
        )
    ), [tgAccountOptions])

    const calendarTagItems = useMemo<IItem<string>[]>(() => (
        getTrialOptionItems(
            calendarTagOptions,
            tag => tag.name,
        )
    ), [calendarTagOptions])

    const tutorItems = useMemo<IItem<string>[]>(() => (
        availableTutors.map(({tutor, conversion, conversionBase, conversionClosedCount, conversionLostCount}) => ({
            id: tutor.userId,
            name: tutor.fullName,
            secondaryText: formatTutorLoad(tutor),
            tertiaryText: formatTutorConversion(conversion, conversionBase, conversionClosedCount, conversionLostCount),
        }))
    ), [availableTutors])

    useEffect(() => {
        if(!filterOptions) return

        setTgAccountOptions(filterOptions.tgAccounts)
        setCalendarTagOptions(filterOptions.tags || [])
    }, [filterOptions])

    useEffect(() => {
        if(!selectedTagId){
            if(tag){
                setTag('')
            }
            return
        }

        const selectedTag = calendarTagOptions.find(item => item.id === selectedTagId)
        if(!selectedTag) return

        if(tag !== selectedTag.name){
            setTag(selectedTag.name)
        }
    }, [selectedTagId, calendarTagOptions, tag])

    useEffect(() => {
        onMount?.()
    }, [onMount])

    const requestOptions = useCallback(async (
        selected: ICalendarFilter,
        nextStage: Exclude<TCreateStage, null>,
    ) => {
        optionsControllerRef.current?.abort()

        const controller = new AbortController()
        optionsControllerRef.current = controller

        try{
            setStage(nextStage)

            const response = await calendarService.getCalendarFilterOptions(
                {selected},
                controller.signal,
            )

            if(nextStage === 'tgAccountId') setTgAccountOptions(response.tgAccounts)
            if(nextStage === 'tagId') setCalendarTagOptions(response.tags || [])
        }
        catch(error){
            if((error as Error).name === 'AbortError') return

            console.log(error)
            if(error instanceof AuthError){
                setIsAuth(false)
                setGlobalMessage({message: error.message, type: 'error'})
            }
            else if(error instanceof MyError){
                setGlobalMessage({message: error.message, type: 'error'})
            }
            else{
                setError('Ошибка при загрузке списков для выбора')
            }
        }
        finally{
            if(!controller.signal.aborted) setStage(null)
        }
    }, [setGlobalMessage, setIsAuth])

    const requestAvailableTutors = useCallback(async (
        nextTgAccountId: string,
        nextTagId: string,
        nextTag: string,
    ) => {
        availableTutorsControllerRef.current?.abort()

        const controller = new AbortController()
        availableTutorsControllerRef.current = controller

        try{
            setStage('userId')

            const response = await trialLessonService.findTrialAvailableTutors({
                slot: {
                    startsAt: startsAt.toISOString(),
                    durationSlots,
                },
                tgAccountId: nextTgAccountId,
                tagId: nextTagId,
                tag: nextTag.trim(),
                tutorGender,
            }, {signal: controller.signal})

            const tutors = filter.userIds.length
                ? response.tutors.filter(({tutor}) => filter.userIds.includes(tutor.userId))
                : response.tutors

            setAvailableTutors(tutors)
            setUserId(currentUserId => {
                if(currentUserId && tutors.some(({tutor}) => tutor.userId === currentUserId)){
                    return currentUserId
                }

                if(
                    initialUserIdRef.current &&
                    tutors.some(({tutor}) => tutor.userId === initialUserIdRef.current)
                ){
                    return initialUserIdRef.current
                }

                return ''
            })
        }
        catch(error){
            if((error as Error).name === 'AbortError') return

            console.log(error)
            if(error instanceof AuthError){
                setIsAuth(false)
                setGlobalMessage({message: error.message, type: 'error'})
            }
            else if(error instanceof MyError){
                setGlobalMessage({message: error.message, type: 'error'})
            }
            else{
                setAvailableTutors([])
                setUserId('')
                setError(getTrialRequestErrorMessage(error, 'Ошибка при поиске доступных исполнителей'))
            }
        }
        finally{
            if(!controller.signal.aborted) setStage(null)
        }
    }, [durationSlots, filter.userIds, setGlobalMessage, setIsAuth, startsAt, tutorGender])

    useEffect(() => {
        const loadInitialOptions = async () => {
            await requestOptions(getTrialCalendarFilter('', ''), 'tgAccountId')

            if(initialTgAccountIdRef.current){
                await requestOptions(getTrialCalendarFilter(initialTgAccountIdRef.current, ''), 'tagId')
            }
        }

        loadInitialOptions()

        return () => {
            optionsControllerRef.current?.abort()
            availableTutorsControllerRef.current?.abort()
        }
    }, [requestOptions])

    useEffect(() => {
        if(!tgAccountId || !selectedTagId){
            availableTutorsControllerRef.current?.abort()
            setAvailableTutors([])
            setUserId('')
            return
        }

        const timeoutId = window.setTimeout(() => {
            requestAvailableTutors(tgAccountId, selectedTagId, tag)
        }, 250)

        return () => window.clearTimeout(timeoutId)
    }, [requestAvailableTutors, selectedTagId, tag, tgAccountId])

    const onSelectTgAccount = (item: IItem<string>) => {
        return (selected: boolean) => {
            setError('')
            const nextTgAccountId = selected ? item.id : ''

            setTgAccountId(nextTgAccountId)
            setSelectedTagId('')
            setTutorGender('TUTOR_GENDER_ANY')
            setUserId('')
            setCalendarTagOptions([])
            setAvailableTutors([])

            if(nextTgAccountId){
                requestOptions(getTrialCalendarFilter(nextTgAccountId, ''), 'tagId')
            }
        }
    }

    const onSelectCalendarTag = (item: IItem<string>) => {
        return (selected: boolean) => {
            setError('')
            setSelectedTagId(selected ? item.id : '')
            setTutorGender('TUTOR_GENDER_ANY')
            onChangeTag(selected ? item.name : '')
        }
    }

    const onSelectTutorGender = (item: IItem<TTutorGender>) => {
        return (selected: boolean) => {
            setError('')
            setTutorGender(selected ? item.id : 'TUTOR_GENDER_ANY')
            setUserId('')
            setAvailableTutors([])
        }
    }

    const onSelectTutor = (item: IItem<string>) => {
        return (selected: boolean) => {
            setError('')
            setUserId(selected ? item.id : '')
        }
    }

    const onChangeTag = (value: string) => {
        setError('')
        setTag(value)
        setUserId('')
        setAvailableTutors([])
    }

    const onCreateTag = async () => {
        const tagName = createTagValue.trim()

        if(!tagName){
            setCreateTagError('Введите название тега')
            return
        }

        try{
            setIsTagCreating(true)
            setCreateTagError('')
            const createdTag = await studentService.createTag(tagName)

            setCalendarTagOptions(current => [...current, {id: String(createdTag.id), name: createdTag.tag_name}]
                .sort((first, second) => first.name.localeCompare(second.name)))
            setSelectedTagId(String(createdTag.id))
            onChangeTag(createdTag.tag_name)
            setCreateTagValue('')
            setIsCreateTagOpen(false)
            setGlobalMessage({message: 'Тег создан', type: 'ok'})
        }
        catch(error){
            console.log(error)
            if(error instanceof AuthError){
                setIsAuth(false)
                setGlobalMessage({message: error.message, type: 'error'})
            }
            else{
                setCreateTagError(error instanceof MyError ? error.message : 'Ошибка при создании тега')
            }
        }
        finally{
            setIsTagCreating(false)
        }
    }

    const onCreateWorkTg = async () => {
        const workTgName = createWorkTgValue.trim()

        if(!workTgName){
            setCreateWorkTgError('Введите название рабочего Telegram')
            return
        }

        try{
            setIsWorkTgCreating(true)
            setCreateWorkTgError('')
            const createdWorkTg = await workTgService.create(workTgName)
            const createdId = String(createdWorkTg.id)

            setTgAccountOptions(current => [
                ...current.filter(item => item.id !== createdId),
                {
                    id: createdId,
                    username: createdWorkTg.name.replace(/^@/, ''),
                    title: createdWorkTg.name,
                },
            ])
            setTgAccountId(createdId)
            setSelectedTagId('')
            setUserId('')
            setCalendarTagOptions([])
            setAvailableTutors([])
            setCreateWorkTgValue('')
            setIsCreateWorkTgOpen(false)
            setGlobalMessage({message: 'Рабочий Telegram создан', type: 'ok'})
            requestOptions(getTrialCalendarFilter(createdId, ''), 'tagId')
        }
        catch(error){
            console.log(error)
            if(error instanceof AuthError){
                setIsAuth(false)
                setGlobalMessage({message: error.message, type: 'error'})
            }
            else{
                setCreateWorkTgError(
                    error instanceof MyError ? error.message : 'Ошибка при создании рабочего Telegram'
                )
            }
        }
        finally{
            setIsWorkTgCreating(false)
        }
    }

    const createTrialLesson = async () => {
        if(!tgAccountId || !selectedTagId || !userId){
            setError('Заполните рабочий Telegram, тег и доступного исполнителя')
            return
        }

        if(!studentName.trim()){
            setError('Укажите ФИО студента')
            return
        }

        try{
            setIsSaving(true)
            setError('')

            await trialLessonService.createTrialLesson({
                slot: {
                    startsAt: startsAt.toISOString(),
                    durationSlots,
                },
                tgAccountId,
                userId,
                tagId: selectedTagId,
                tag: tag.trim(),
                studentName: studentName.trim(),
                studentTg: studentTg.trim(),
                privateInfo: privateInfo.trim(),
                publicComment: publicComment.trim(),
            })

            setGlobalMessage({message: 'Пробное занятие создано', type: 'ok'})
            setUserId('')
            setStudentName('')
            setStudentTg('')
            setPrivateInfo('')
            setPublicComment('')
            onCreated()
        }
        catch(error){
            console.log(error)
            if(error instanceof AuthError){
                setIsAuth(false)
                setGlobalMessage({message: error.message, type: 'error'})
            }
            else if(error instanceof MyError){
                setGlobalMessage({message: error.message, type: 'error'})
            }
            else{
                setError(getTrialRequestErrorMessage(error, 'Ошибка при создании пробного занятия'))
            }
        }
        finally{
            setIsSaving(false)
        }
    }

    return (
        <section className={classes.container}>
            <section className={classes.header}>
                <button type="button" className={classes.backButton} onClick={onCancel} disabled={isSaving}>
                    <span>‹</span>
                    Назад
                </button>

                <section className={classes.headerContent}>
                    <h2 className={classes.title}>Создание пробного занятия</h2>
                    <p className={classes.time}>{formatTime(startsAt)}–{formatTime(endsAt)}</p>
                </section>
            </section>

            <section className={classes.form}>
                <section className={classes.filtersSection}>
                    <h3 className={classes.sectionTitle}>Параметры занятия</h3>
                    <section className={classes.formGrid}>
                        <section className={classes.field}>
                            <span className={classes.label}>Рабочий Telegram</span>
                            <span className={classes.hint}>Выберите рабочий Telegram</span>
                            <DropDownListSelected
                                isLoading={stage === 'tgAccountId'}
                                items={tgAccountItems}
                                selectedIdItems={getTrialSelectedIdItems(tgAccountId)}
                                onSelected={onSelectTgAccount}
                                search
                                emptyText="Нет доступных Telegram"
                            />
                            {canCreateWorkTg && (
                                <button
                                    type="button"
                                    className={classes.createOptionButton}
                                    onClick={() => {
                                        setCreateWorkTgError('')
                                        setIsCreateWorkTgOpen(true)
                                    }}
                                >
                                    Создать рабочий Telegram
                                </button>
                            )}
                        </section>

                        {Boolean(tgAccountId) && (
                            <section className={classes.field}>
                                <span className={classes.label}>Тег</span>
                                <span className={classes.hint}>Выберите тег для пробного занятия</span>
                                <DropDownListSelected
                                    isLoading={stage === 'tagId'}
                                    items={calendarTagItems}
                                    selectedIdItems={getTrialSelectedIdItems(selectedTagId)}
                                    onSelected={onSelectCalendarTag}
                                    search
                                    searchPlaceholder="Поиск тега"
                                    placeholder="Выберите тег"
                                    emptyText="Нет студентов или исполнителей с тегами на тг"
                                />
                                {canCreateTag && (
                                    <button
                                        type="button"
                                        className={classes.createOptionButton}
                                        onClick={() => {
                                            setCreateTagError('')
                                            setIsCreateTagOpen(true)
                                        }}
                                    >
                                        Создать новый тег
                                    </button>
                                )}
                            </section>
                        )}

                        {Boolean(selectedTagId) && (
                            <section className={classes.field}>
                                <span className={classes.label}>Пол исполнителя</span>
                                <span className={classes.hint}>Уточните предпочтительный пол исполнителя</span>
                                <DropDownListSelected
                                    isLoading={false}
                                    items={GENDER_ITEMS}
                                    selectedIdItems={tutorGender ? [tutorGender] : []}
                                    onSelected={onSelectTutorGender}
                                    placeholder="Выберите пол"
                                />
                            </section>
                        )}

                        {Boolean(selectedTagId) && (
                            <section className={classes.field}>
                                <span className={classes.label}>Исполнитель</span>
                                <span className={classes.hint}>Показываются только исполнители, свободные в выбранный слот</span>
                                <DropDownListSelected
                                    isLoading={stage === 'userId'}
                                    items={tutorItems}
                                    selectedIdItems={getTrialSelectedIdItems(userId)}
                                    onSelected={onSelectTutor}
                                    search
                                    placeholder="Выберите исполнителя"
                                    emptyText="Нет доступных исполнителей на это время"
                                />
                            </section>
                        )}
                    </section>
                </section>

                {Boolean(userId) && (
                    <section className={classes.detailsSection}>
                        <h3 className={classes.sectionTitle}>Данные пробного занятия</h3>
                        <section className={classes.formGrid}>
                            <section className={classes.field}>
                                <MyInput
                                    id="trial-create-student-name"
                                    title="ФИО студента"
                                    value={studentName}
                                    setValue={setStudentName}
                                    setError={setError}
                                    isLoading={isSaving}
                                    placeholder="Введите ФИО студента"
                                />
                            </section>

                            <section className={classes.field}>
                                <MyInput
                                    id="trial-create-student-tg"
                                    title="Telegram студента"
                                    value={studentTg}
                                    setValue={setStudentTg}
                                    setError={setError}
                                    isLoading={isSaving}
                                    placeholder="username без @"
                                />
                            </section>
                        </section>

                        <section className={classes.field}>
                            <MyTextarea
                                id="trial-create-public-comment"
                                title="Публичный комментарий"
                                value={publicComment}
                                setValue={setPublicComment}
                                setError={setError}
                                disabled={isSaving}
                            />
                        </section>

                        <section className={classes.field}>
                            <MyTextarea
                                id="trial-create-private-info"
                                title="Приватная информация"
                                value={privateInfo}
                                setValue={setPrivateInfo}
                                setError={setError}
                                disabled={isSaving}
                            />
                        </section>
                    </section>
                )}
            </section>

            <section className={classes.actions}>
                <MyButton isLoading={isSaving} error={error} onClick={createTrialLesson}>Создать</MyButton>
            </section>

            <TrialCreateOptionModal
                open={isCreateTagOpen}
                setOpen={setIsCreateTagOpen}
                title="Создать тег"
                placeholder="Например: ОГЭ"
                value={createTagValue}
                setValue={setCreateTagValue}
                error={createTagError}
                setError={setCreateTagError}
                isLoading={isTagCreating}
                onCreate={onCreateTag}
            />
            <TrialCreateOptionModal
                open={isCreateWorkTgOpen}
                setOpen={setIsCreateWorkTgOpen}
                title="Создать рабочий Telegram"
                placeholder="Например: @Rep1332"
                value={createWorkTgValue}
                setValue={setCreateWorkTgValue}
                error={createWorkTgError}
                setError={setCreateWorkTgError}
                isLoading={isWorkTgCreating}
                onCreate={onCreateWorkTg}
            />
        </section>
    )
}
